import React, { useRef, useState, useCallback, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Scissors,
  Sliders,
  Type,
  Gauge,
  Crop,
  Volume2,
  RotateCw,
  Play,
  Pause,
  Undo2,
  Redo2,
  Download,
  ArrowLeft,
  Loader2,
  VolumeX,
  FlipHorizontal,
  FlipVertical,
  Plus,
  Trash2,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { mediaAPI, getErrorMessage } from '../../services/api';

/* ─── Types ─── */
type ToolType = 'trim' | 'filters' | 'text' | 'speed' | 'crop' | 'audio' | 'rotate' | null;

interface TextOverlay {
  id: string;
  text: string;
  x: number;
  y: number;
  fontSize: number;
  color: string;
  startTime: number;
  endTime: number;
}

interface VideoEditState {
  // Trim
  startTime: number;
  endTime: number;
  // Filters
  brightness: number;
  contrast: number;
  saturation: number;
  // Speed
  speed: number;
  // Crop (percentages)
  cropX: number;
  cropY: number;
  cropW: number;
  cropH: number;
  cropActive: boolean;
  // Rotation
  rotation: number;
  flipH: boolean;
  flipV: boolean;
  // Text overlays
  textOverlays: TextOverlay[];
  // Audio
  muteAudio: boolean;
  volume: number;
}

interface MediaData {
  id: string;
  original_url: string;
  duration: number | null;
  width: number | null;
  height: number | null;
  media_type: string;
  file_size: number;
}

/* ─── Defaults ─── */
const defaultEditState: VideoEditState = {
  startTime: 0,
  endTime: 0,
  brightness: 100,
  contrast: 100,
  saturation: 100,
  speed: 1,
  cropX: 0,
  cropY: 0,
  cropW: 100,
  cropH: 100,
  cropActive: false,
  rotation: 0,
  flipH: false,
  flipV: false,
  textOverlays: [],
  muteAudio: false,
  volume: 100,
};

/* ─── Filter Presets ─── */
const getFilterPresets = (t: (key: string, fallback: string) => string) => [
  {
    name: 'normal',
    label: t('veditor_filter_normal', 'Normal'),
    brightness: 100,
    contrast: 100,
    saturation: 100,
  },
  {
    name: 'bw',
    label: t('veditor_filter_bw', 'B&W'),
    brightness: 100,
    contrast: 100,
    saturation: 0,
  },
  {
    name: 'warm',
    label: t('veditor_filter_warm', 'Warm'),
    brightness: 105,
    contrast: 100,
    saturation: 120,
  },
  {
    name: 'cool',
    label: t('veditor_filter_cool', 'Cool'),
    brightness: 98,
    contrast: 100,
    saturation: 90,
  },
  {
    name: 'vivid',
    label: t('veditor_filter_vivid', 'Vivid'),
    brightness: 100,
    contrast: 115,
    saturation: 160,
  },
  {
    name: 'dramatic',
    label: t('veditor_filter_dramatic', 'Dramatic'),
    brightness: 90,
    contrast: 140,
    saturation: 80,
  },
  {
    name: 'vintage',
    label: t('veditor_filter_vintage', 'Vintage'),
    brightness: 95,
    contrast: 90,
    saturation: 60,
  },
  {
    name: 'fade',
    label: t('veditor_filter_fade', 'Fade'),
    brightness: 110,
    contrast: 80,
    saturation: 80,
  },
];

/* ─── Crop Presets ─── */
const getCropPresets = (t: (key: string, fallback: string) => string) => [
  { label: t('veditor_crop_free', 'Free'), ratio: 0 },
  { label: '1:1', ratio: 1 },
  { label: '4:5', ratio: 4 / 5 },
  { label: '9:16', ratio: 9 / 16 },
  { label: '16:9', ratio: 16 / 9 },
];

/* ─── Speed Presets ─── */
const SPEED_PRESETS = [0.25, 0.5, 1, 1.5, 2, 4];

/* makeAPIRequest removed — use mediaAPI from services/api */

/* ═══════════════════════════════════════════════════════════
   Timeline Component
   ═══════════════════════════════════════════════════════════ */
interface TimelineProps {
  duration: number;
  currentTime: number;
  startTime: number;
  endTime: number;
  textOverlays: TextOverlay[];
  onPlayheadChange: (time: number) => void;
  onStartTimeChange: (time: number) => void;
  onEndTimeChange: (time: number) => void;
  t: (key: string, fallback: string) => string;
}

const Timeline: React.FC<TimelineProps> = ({
  duration,
  currentTime,
  startTime,
  endTime,
  textOverlays,
  onPlayheadChange,
  onStartTimeChange,
  onEndTimeChange,
  t,
}) => {
  const timelineRef = useRef<HTMLDivElement>(null);
  const [draggingHandle, setDraggingHandle] = useState<'start' | 'end' | null>(null);

  const handleTimelineClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!timelineRef.current || draggingHandle) {
        return;
      }
      const rect = timelineRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const ratio = x / rect.width;
      const time = Math.max(0, Math.min(duration, ratio * duration));
      onPlayheadChange(time);
    },
    [duration, onPlayheadChange, draggingHandle]
  );

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>, handle: 'start' | 'end') => {
    e.stopPropagation();
    setDraggingHandle(handle);
  };

  useEffect(() => {
    if (draggingHandle === null || !timelineRef.current) {
      return;
    }

    const handleMouseMove = (e: MouseEvent) => {
      const rect = timelineRef.current!.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const ratio = Math.max(0, Math.min(1, x / rect.width));
      const time = ratio * duration;

      if (draggingHandle === 'start') {
        onStartTimeChange(Math.min(time, endTime));
      } else {
        onEndTimeChange(Math.max(time, startTime));
      }
    };

    const handleMouseUp = () => {
      setDraggingHandle(null);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [draggingHandle, duration, startTime, endTime, onStartTimeChange, onEndTimeChange]);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const playheadPercent = (currentTime / duration) * 100;
  const startPercent = (startTime / duration) * 100;
  const endPercent = (endTime / duration) * 100;

  // Generate time markers every 5 seconds
  const timeMarkers = [];
  for (let i = 0; i <= duration; i += 5) {
    timeMarkers.push(i);
  }

  return (
    <div className="space-y-2">
      {/* Current time display */}
      <div className="text-xs text-slate-300 flex justify-between">
        <span>{formatTime(currentTime)}</span>
        <span>{formatTime(duration)}</span>
      </div>

      {/* Timeline */}
      <div
        ref={timelineRef}
        className="relative h-16 bg-slate-700 rounded-lg overflow-hidden cursor-pointer border border-slate-600"
        onClick={handleTimelineClick}
        onKeyDown={(e) => {
          if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
            e.preventDefault();
            const newTime =
              e.key === 'ArrowLeft'
                ? Math.max(0, currentTime - 1)
                : Math.min(duration, currentTime + 1);
            onPlayheadChange(newTime);
          }
        }}
        role="slider"
        aria-label={t('veditor_timeline', 'Timeline')}
        aria-valuenow={Math.round(currentTime * 10) / 10}
        aria-valuemin={0}
        aria-valuemax={Math.round(duration * 10) / 10}
        tabIndex={0}
      >
        {/* Time markers */}
        {timeMarkers.map((marker) => (
          <div
            key={marker}
            className="absolute top-0 bottom-0 w-px bg-slate-600"
            style={{ left: `${(marker / duration) * 100}%` }}
          >
            <div className="text-xs text-slate-400 absolute top-1 -left-4 w-8 text-center">
              {Math.floor(marker / 60)}:{Math.floor(marker % 60)
                .toString()
                .padStart(2, '0')}
            </div>
          </div>
        ))}

        {/* Trimmed region (lighter background) */}
        <div
          className="absolute top-0 bottom-0 bg-slate-800 opacity-50"
          style={{ left: '0', right: `${100 - startPercent}%` }}
        />
        <div
          className="absolute top-0 bottom-0 bg-slate-800 opacity-50"
          style={{ left: `${endPercent}%`, right: '0' }}
        />

        {/* Text overlay indicators */}
        {(textOverlays || []).map((overlay) => {
          const overlayStart = (overlay.startTime / duration) * 100;
          const overlayEnd = (overlay.endTime / duration) * 100;
          return (
            <div
              key={overlay.id}
              className="absolute top-1/2 transform -translate-y-1/2 bg-purple-500 opacity-70 text-xs text-white flex items-center px-1"
              style={{
                left: `${overlayStart}%`,
                width: `${overlayEnd - overlayStart}%`,
                height: '8px',
              }}
              title={overlay.text}
            />
          );
        })}

        {/* Trim handles */}
        <div
          className="absolute top-0 bottom-0 w-1 bg-blue-400 cursor-ew-resize hover:bg-blue-300"
          style={{ left: `${startPercent}%` }}
          onMouseDown={(e) => handleMouseDown(e, 'start')}
          role="button"
          tabIndex={0}
          aria-label={t('veditor_trim_start', 'Trim start')}
          onKeyDown={(e) => {
            if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
              e.preventDefault();
              const delta = e.key === 'ArrowLeft' ? -0.1 : 0.1;
              onStartTimeChange(Math.min(endTime, Math.max(0, startTime + delta)));
            }
          }}
        />
        <div
          className="absolute top-0 bottom-0 w-1 bg-blue-400 cursor-ew-resize hover:bg-blue-300"
          style={{ left: `${endPercent}%` }}
          onMouseDown={(e) => handleMouseDown(e, 'end')}
          role="button"
          tabIndex={0}
          aria-label={t('veditor_trim_end', 'Trim end')}
          onKeyDown={(e) => {
            if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
              e.preventDefault();
              const delta = e.key === 'ArrowLeft' ? -0.1 : 0.1;
              onEndTimeChange(Math.max(startTime, Math.min(duration, endTime + delta)));
            }
          }}
        />

        {/* Playhead */}
        <div
          className="absolute top-0 bottom-0 w-1 bg-blue-500 pointer-events-none"
          style={{ left: `${playheadPercent}%` }}
        />
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   Main Component
   ═══════════════════════════════════════════════════════════ */
const VideoEditorPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const mediaId = searchParams.get('mediaId');
  const returnTo = searchParams.get('returnTo');
  const source = searchParams.get('source');
  const selectedPlatformsParam = searchParams.get('selected_platforms') || '';
  const editorSlotIndex = searchParams.get('slot_index');
  const editorBaseMediaId = searchParams.get('base_media_id');
  const editorDraftKey = searchParams.get('draft_key');

  /* ─── State ─── */
  const [edit, setEdit] = useState<VideoEditState>(defaultEditState);
  const [activeTool, setActiveTool] = useState<ToolType>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [undoStack, setUndoStack] = useState<VideoEditState[]>([]);
  const [redoStack, setRedoStack] = useState<VideoEditState[]>([]);
  const [newTextInput, setNewTextInput] = useState('');
  const [textColor, setTextColor] = useState('#ffffff');
  const [textFontSize, setTextFontSize] = useState(24);

  const videoRef = useRef<HTMLVideoElement>(null);

  /* ─── Queries ─── */
  const mediaQuery = useQuery<MediaData>({
    queryKey: ['media', mediaId],
    queryFn: async () => {
      if (!mediaId) {
        throw new Error('Media ID required');
      }
      const response = await mediaAPI.get(mediaId);
      return response.data;
    },
    enabled: !!mediaId,
    staleTime: Infinity,
  });

  const media = mediaQuery.data;

  /* ─── Initialize edit state when media loads ─── */
  const mediaDuration = media?.duration ?? 0;
  useEffect(() => {
    if (media && edit.endTime === 0 && mediaDuration > 0) {
      setEdit((prev) => ({
        ...prev,
        endTime: mediaDuration,
      }));
    }
  }, [media, edit.endTime, mediaDuration]);

  /* ─── Mutations ─── */
  const exportMutation = useMutation({
    mutationFn: async (editState: VideoEditState) => {
      if (!mediaId || !media) {
        throw new Error('Missing media');
      }

      // Build payload with only non-default values
      const payload: Record<string, unknown> = {};

      if (editState.startTime > 0 || editState.endTime < mediaDuration) {
        if (editState.startTime > 0) {
          payload.start_time = editState.startTime;
        }
        if (editState.endTime < mediaDuration) {
          payload.end_time = editState.endTime;
        }
      }

      if (editState.brightness !== 100) {
        payload.brightness = (editState.brightness - 100) / 100;
      }
      if (editState.contrast !== 100) {
        payload.contrast = editState.contrast / 100;
      }
      if (editState.saturation !== 100) {
        payload.saturation = editState.saturation / 100;
      }

      if (editState.speed !== 1) {
        payload.speed = editState.speed;
      }

      if (editState.rotation !== 0) {
        payload.rotation = editState.rotation;
      }
      if (editState.flipH) {
        payload.flip_horizontal = true;
      }
      if (editState.flipV) {
        payload.flip_vertical = true;
      }

      if (editState.muteAudio) {
        payload.mute_audio = true;
      }
      if (editState.volume !== 100) {
        payload.volume = editState.volume / 100;
      }

      if (editState.cropActive) {
        payload.crop_x = editState.cropX;
        payload.crop_y = editState.cropY;
        payload.crop_w = editState.cropW;
        payload.crop_h = editState.cropH;
      }

      if ((editState.textOverlays || []).length > 0) {
        payload.text_overlays = (editState.textOverlays || []).map((o) => ({
          text: o.text,
          x: o.x,
          y: o.y,
          font_size: o.fontSize,
          color: o.color,
          start_time: o.startTime,
          end_time: o.endTime,
        }));
      }

      return mediaAPI.videoEdit(mediaId, payload);
    },
    onSuccess: (response) => {
      toast.success(t('veditor_export_success', 'Video exported successfully'));
      setTimeout(() => {
        const editedMediaId = (response?.data as { media_id?: string } | undefined)?.media_id;
        if (returnTo) {
          const separator = returnTo.includes('?') ? '&' : '?';
          const returnsToDraft = source === 'compose' || source === 'studio';
          const nextParams = new URLSearchParams();
          if (editedMediaId && returnsToDraft) {
            nextParams.set('editor_media_id', editedMediaId);
            nextParams.set('editor_apply_mode', 'replace_current_media');
            if (editorSlotIndex) {
              nextParams.set('editor_slot_index', editorSlotIndex);
            }
            if (editorBaseMediaId) {
              nextParams.set('editor_base_media_id', editorBaseMediaId);
            }
            if (selectedPlatformsParam) {
              nextParams.set('editor_target_platforms', selectedPlatformsParam);
            }
            if (editorDraftKey) {
              nextParams.set('draft_key', editorDraftKey);
            }
          } else if (editedMediaId) {
            nextParams.set('newMediaId', editedMediaId);
          }
          navigate(
            `${returnTo}${nextParams.toString() ? `${separator}${nextParams.toString()}` : ''}`
          );
          return;
        }
        navigate(-1);
      }, 1000);
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, t('veditor_export_failed', 'Failed to export video')));
    },
  });

  /* ─── Undo/Redo ─── */
  const saveHistory = useCallback(() => {
    setUndoStack((prev) => [...prev, edit]);
    setRedoStack([]);
  }, [edit]);

  const handleUndo = useCallback(() => {
    if (undoStack.length === 0) {
      return;
    }
    const newStack = [...undoStack];
    const previousState = newStack.pop();
    if (previousState) {
      setRedoStack((prev) => [...prev, edit]);
      setEdit(previousState);
      setUndoStack(newStack);
    }
  }, [undoStack, edit]);

  const handleRedo = useCallback(() => {
    if (redoStack.length === 0) {
      return;
    }
    const newStack = [...redoStack];
    const nextState = newStack.pop();
    if (nextState) {
      setUndoStack((prev) => [...prev, edit]);
      setEdit(nextState);
      setRedoStack(newStack);
    }
  }, [redoStack, edit]);

  /* ─── Keyboard shortcuts ─── */
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.code === 'Space') {
        e.preventDefault();
        setIsPlaying((prev) => !prev);
      }
      if (e.ctrlKey || e.metaKey) {
        if (e.key === 'z' && !e.shiftKey) {
          e.preventDefault();
          handleUndo();
        }
        if ((e.key === 'z' && e.shiftKey) || e.key === 'y') {
          e.preventDefault();
          handleRedo();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleUndo, handleRedo]);

  /* ─── Video playback ─── */
  useEffect(() => {
    if (!videoRef.current) {
      return;
    }

    if (isPlaying) {
      videoRef.current.play().catch(() => {
        // Autoplay prevented, just continue
      });
    } else {
      videoRef.current.pause();
    }
  }, [isPlaying]);

  const handleTimeUpdate = useCallback(() => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  }, []);

  const handlePlayPauseClick = () => {
    setIsPlaying((prev) => !prev);
  };

  const handleSkipBackward = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = Math.max(0, videoRef.current.currentTime - 5);
    }
  };

  const handleSkipForward = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = Math.min(
        media?.duration || 0,
        videoRef.current.currentTime + 5
      );
    }
  };

  /* ─── Tool handlers ─── */
  const updateEdit = (updates: Partial<VideoEditState>) => {
    saveHistory();
    setEdit((prev) => ({ ...prev, ...updates }));
  };

  const applyFilterPreset = (preset: ReturnType<typeof getFilterPresets>[0]) => {
    saveHistory();
    setEdit((prev) => ({
      ...prev,
      brightness: preset.brightness,
      contrast: preset.contrast,
      saturation: preset.saturation,
    }));
  };

  const applyCropPreset = (ratio: number) => {
    if (ratio === 0) {
      // Free crop
      updateEdit({ cropActive: false });
    } else {
      // Fixed aspect ratio
      const videoW = media?.width || 1920;
      const videoH = media?.height || 1080;
      const videoRatio = videoW / videoH;

      let w = 100;
      let h = 100;

      if (ratio > videoRatio) {
        // Crop width
        w = (videoRatio / ratio) * 100;
      } else {
        // Crop height
        h = (ratio / videoRatio) * 100;
      }

      updateEdit({
        cropActive: true,
        cropX: (100 - w) / 2,
        cropY: (100 - h) / 2,
        cropW: w,
        cropH: h,
      });
    }
  };

  const applySpeedPreset = (speed: number) => {
    updateEdit({ speed });
  };

  const addTextOverlay = () => {
    if (!newTextInput.trim() || !media) {
      return;
    }

    saveHistory();
    const newOverlay: TextOverlay = {
      id: `text-${Date.now()}`,
      text: newTextInput,
      x: 50,
      y: 50,
      fontSize: textFontSize,
      color: textColor,
      startTime: currentTime,
      endTime: Math.min(currentTime + 5, mediaDuration),
    };

    setEdit((prev) => ({
      ...prev,
      textOverlays: [...(prev.textOverlays || []), newOverlay],
    }));

    setNewTextInput('');
    setActiveTool(null);
  };

  const removeTextOverlay = (id: string) => {
    saveHistory();
    setEdit((prev) => ({
      ...prev,
      textOverlays: (prev.textOverlays || []).filter((o) => o.id !== id),
    }));
  };

  if (mediaQuery.isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-900">
        <Loader2 className="w-12 h-12 animate-spin text-blue-500" />
      </div>
    );
  }

  if (mediaQuery.isError || !media) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-slate-900 gap-4">
        <p className="text-red-400">{t('veditor_error_loading', 'Error loading video')}</p>
        <button
          onClick={() => navigate(-1)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          {t('common_back', 'Back')}
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-slate-900 text-slate-100">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700 bg-slate-800">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
            aria-label={t('common_back', 'Back')}
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-xl font-semibold">{t('veditor_title', 'Video Editor')}</h1>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={handleUndo}
            disabled={undoStack.length === 0}
            className="p-2 hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
            aria-label={t('common_undo', 'Undo')}
          >
            <Undo2 className="w-5 h-5" />
          </button>
          <button
            onClick={handleRedo}
            disabled={redoStack.length === 0}
            className="p-2 hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
            aria-label={t('common_redo', 'Redo')}
          >
            <Redo2 className="w-5 h-5" />
          </button>
          <button
            onClick={() => exportMutation.mutate(edit)}
            disabled={exportMutation.isPending}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
          >
            {exportMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {t('common_exporting', 'Exporting...')}
              </>
            ) : (
              <>
                <Download className="w-4 h-4" />
                {t('common_export', 'Export')}
              </>
            )}
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left toolbar */}
        <div className="w-14 bg-slate-800 border-r border-slate-700 flex flex-col items-center py-4 gap-2">
          {[
            { type: 'trim' as ToolType, icon: Scissors, label: t('veditor_trim', 'Trim') },
            { type: 'filters' as ToolType, icon: Sliders, label: t('veditor_filters', 'Filters') },
            { type: 'text' as ToolType, icon: Type, label: t('veditor_text', 'Text') },
            { type: 'speed' as ToolType, icon: Gauge, label: t('veditor_speed', 'Speed') },
            { type: 'crop' as ToolType, icon: Crop, label: t('veditor_crop', 'Crop') },
            { type: 'audio' as ToolType, icon: Volume2, label: t('veditor_audio', 'Audio') },
            { type: 'rotate' as ToolType, icon: RotateCw, label: t('veditor_rotate', 'Rotate') },
          ].map(({ type, icon: Icon, label }) => (
            <button
              key={type}
              onClick={() => setActiveTool(activeTool === type ? null : type)}
              className={`p-3 rounded-lg transition-colors ${
                activeTool === type
                  ? 'bg-blue-600 text-white'
                  : 'text-slate-400 hover:text-slate-200 hover:bg-slate-700'
              }`}
              aria-label={label}
              title={label}
            >
              <Icon className="w-5 h-5" />
            </button>
          ))}
        </div>

        {/* Center: Video preview and timeline */}
        <div className="flex-1 flex flex-col items-center justify-center p-6 overflow-y-auto">
          <div className="w-full max-w-2xl space-y-4">
            {/* Video preview */}
            <div className="relative bg-black rounded-lg overflow-hidden aspect-video flex items-center justify-center">
              {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
              <video
                ref={videoRef}
                src={media.original_url}
                onTimeUpdate={handleTimeUpdate}
                className="w-full h-full object-contain"
                style={{
                  filter: `brightness(${edit.brightness / 100}) contrast(${edit.contrast / 100}) saturate(${edit.saturation / 100})`,
                  transform: `rotate(${edit.rotation}deg) scaleX(${edit.flipH ? -1 : 1}) scaleY(${edit.flipV ? -1 : 1})`,
                }}
              />

              {/* Play/pause overlay */}
              {!isPlaying && (
                <button
                  onClick={handlePlayPauseClick}
                  className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/50 transition-colors"
                  aria-label={t('common_play', 'Play')}
                >
                  <Play className="w-16 h-16 text-white" />
                </button>
              )}
            </div>

            {/* Video controls */}
            <div className="flex items-center justify-center gap-3 bg-slate-800 rounded-lg p-3">
              <button
                onClick={handleSkipBackward}
                className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
                aria-label={t('veditor_skip_back', 'Skip backward 5s')}
              >
                <SkipBack className="w-5 h-5" />
              </button>

              <button
                onClick={handlePlayPauseClick}
                className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
                aria-label={isPlaying ? t('common_pause', 'Pause') : t('common_play', 'Play')}
              >
                {isPlaying ? (
                  <Pause className="w-5 h-5" />
                ) : (
                  <Play className="w-5 h-5" />
                )}
              </button>

              <button
                onClick={handleSkipForward}
                className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
                aria-label={t('veditor_skip_forward', 'Skip forward 5s')}
              >
                <SkipForward className="w-5 h-5" />
              </button>

              <div className="flex-1 flex items-center gap-2">
                <Volume2 className="w-4 h-4 text-slate-400" />
                <input
                  type="range"
                  min="0"
                  max="200"
                  value={edit.volume}
                  onChange={(e) => updateEdit({ volume: parseInt(e.target.value) })}
                  className="flex-1 h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-blue-500"
                  aria-label={t('veditor_volume', 'Volume')}
                />
                <span className="text-xs text-slate-400 w-8 text-right">{edit.volume}%</span>
              </div>
            </div>

            {/* Timeline */}
            <Timeline
              duration={mediaDuration}
              currentTime={currentTime}
              startTime={edit.startTime}
              endTime={edit.endTime}
              textOverlays={edit.textOverlays || []}
              onPlayheadChange={(time) => {
                if (videoRef.current) {
                  videoRef.current.currentTime = time;
                }
              }}
              onStartTimeChange={(time) => updateEdit({ startTime: time })}
              onEndTimeChange={(time) => updateEdit({ endTime: time })}
              t={t}
            />
          </div>
        </div>

        {/* Right panel: Tool controls */}
        <div className="w-72 bg-slate-800 border-l border-slate-700 p-4 overflow-y-auto">
          {!activeTool && (
            <div className="text-center text-slate-400 text-sm py-8">
              {t('veditor_select_tool', 'Select a tool to get started')}
            </div>
          )}

          {/* Trim Tool */}
          {activeTool === 'trim' && (
            <div className="space-y-4">
              <h3 className="font-semibold text-slate-100">{t('veditor_trim', 'Trim')}</h3>
              <div>
                <label
                  htmlFor="trim-start"
                  className="block text-sm text-slate-300 mb-2"
                >
                  {t('veditor_start_time', 'Start Time')}
                </label>
                <input
                  id="trim-start"
                  type="number"
                  min="0"
                  max={edit.endTime}
                  step="0.1"
                  value={edit.startTime.toFixed(2)}
                  onChange={(e) => updateEdit({ startTime: parseFloat(e.target.value) })}
                  className="w-full px-3 py-2 bg-slate-700 text-slate-100 rounded-lg border border-slate-600 focus:border-blue-500 focus:outline-none"
                />
              </div>

              <div>
                <label
                  htmlFor="trim-end"
                  className="block text-sm text-slate-300 mb-2"
                >
                  {t('veditor_end_time', 'End Time')}
                </label>
                <input
                  id="trim-end"
                  type="number"
                  min={edit.startTime}
                  max={mediaDuration}
                  step="0.1"
                  value={edit.endTime.toFixed(2)}
                  onChange={(e) => updateEdit({ endTime: parseFloat(e.target.value) })}
                  className="w-full px-3 py-2 bg-slate-700 text-slate-100 rounded-lg border border-slate-600 focus:border-blue-500 focus:outline-none"
                />
              </div>

              <button
                onClick={() => {
                  if (videoRef.current) {
                    updateEdit({ startTime: videoRef.current.currentTime });
                  }
                }}
                className="w-full px-3 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm transition-colors"
              >
                {t('veditor_set_start_at_playhead', 'Set start at playhead')}
              </button>

              <button
                onClick={() => {
                  if (videoRef.current) {
                    updateEdit({ endTime: videoRef.current.currentTime });
                  }
                }}
                className="w-full px-3 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm transition-colors"
              >
                {t('veditor_set_end_at_playhead', 'Set end at playhead')}
              </button>
            </div>
          )}

          {/* Filters Tool */}
          {activeTool === 'filters' && (
            <div className="space-y-4">
              <h3 className="font-semibold text-slate-100">{t('veditor_filters', 'Filters')}</h3>

              {/* Filter presets */}
              <div>
                <label className="block text-sm text-slate-300 mb-2">
                  {t('veditor_filter_presets', 'Presets')}
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {getFilterPresets(t).map((preset) => (
                    <button
                      key={preset.name}
                      onClick={() => applyFilterPreset(preset)}
                      className="px-2 py-1 bg-slate-700 hover:bg-slate-600 rounded text-xs transition-colors"
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Brightness slider */}
              <div>
                <label
                  htmlFor="brightness"
                  className="block text-sm text-slate-300 mb-2"
                >
                  {t('veditor_brightness', 'Brightness')}
                </label>
                <div className="flex items-center gap-3">
                  <input
                    id="brightness"
                    type="range"
                    min="0"
                    max="200"
                    value={edit.brightness}
                    onChange={(e) => updateEdit({ brightness: parseInt(e.target.value) })}
                    className="flex-1 h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-blue-500"
                  />
                  <span className="text-sm text-slate-300 w-8 text-right">
                    {edit.brightness}%
                  </span>
                </div>
              </div>

              {/* Contrast slider */}
              <div>
                <label
                  htmlFor="contrast"
                  className="block text-sm text-slate-300 mb-2"
                >
                  {t('veditor_contrast', 'Contrast')}
                </label>
                <div className="flex items-center gap-3">
                  <input
                    id="contrast"
                    type="range"
                    min="0"
                    max="200"
                    value={edit.contrast}
                    onChange={(e) => updateEdit({ contrast: parseInt(e.target.value) })}
                    className="flex-1 h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-blue-500"
                  />
                  <span className="text-sm text-slate-300 w-8 text-right">{edit.contrast}%</span>
                </div>
              </div>

              {/* Saturation slider */}
              <div>
                <label
                  htmlFor="saturation"
                  className="block text-sm text-slate-300 mb-2"
                >
                  {t('veditor_saturation', 'Saturation')}
                </label>
                <div className="flex items-center gap-3">
                  <input
                    id="saturation"
                    type="range"
                    min="0"
                    max="200"
                    value={edit.saturation}
                    onChange={(e) => updateEdit({ saturation: parseInt(e.target.value) })}
                    className="flex-1 h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-blue-500"
                  />
                  <span className="text-sm text-slate-300 w-8 text-right">
                    {edit.saturation}%
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Text Tool */}
          {activeTool === 'text' && (
            <div className="space-y-4">
              <h3 className="font-semibold text-slate-100">{t('veditor_text', 'Text')}</h3>

              <div>
                <label
                  htmlFor="text-input"
                  className="block text-sm text-slate-300 mb-2"
                >
                  {t('veditor_text_content', 'Text Content')}
                </label>
                <input
                  id="text-input"
                  type="text"
                  value={newTextInput}
                  onChange={(e) => setNewTextInput(e.target.value)}
                  placeholder={t('veditor_enter_text', 'Enter text...')}
                  className="w-full px-3 py-2 bg-slate-700 text-slate-100 rounded-lg border border-slate-600 focus:border-blue-500 focus:outline-none"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      addTextOverlay();
                    }
                  }}
                />
              </div>

              <div>
                <label
                  htmlFor="text-color"
                  className="block text-sm text-slate-300 mb-2"
                >
                  {t('veditor_color', 'Color')}
                </label>
                <div className="flex items-center gap-2">
                  <input
                    id="text-color"
                    type="color"
                    value={textColor}
                    onChange={(e) => setTextColor(e.target.value)}
                    className="w-10 h-10 rounded cursor-pointer"
                  />
                  <span className="text-sm text-slate-300">{textColor}</span>
                </div>
              </div>

              <div>
                <label
                  htmlFor="text-size"
                  className="block text-sm text-slate-300 mb-2"
                >
                  {t('veditor_font_size', 'Font Size')}
                </label>
                <div className="flex items-center gap-3">
                  <input
                    id="text-size"
                    type="range"
                    min="8"
                    max="120"
                    value={textFontSize}
                    onChange={(e) => setTextFontSize(parseInt(e.target.value))}
                    className="flex-1 h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-blue-500"
                  />
                  <span className="text-sm text-slate-300 w-8 text-right">{textFontSize}</span>
                </div>
              </div>

              <button
                onClick={addTextOverlay}
                disabled={!newTextInput.trim()}
                className="w-full px-3 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-sm transition-colors flex items-center justify-center gap-2"
              >
                <Plus className="w-4 h-4" />
                {t('veditor_add_text', 'Add Text')}
              </button>

              {/* Text overlays list */}
              {(edit.textOverlays || []).length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-slate-300">
                    {t('veditor_text_overlays', 'Text Overlays')}
                  </h4>
                  {(edit.textOverlays || []).map((overlay) => (
                    <div
                      key={overlay.id}
                      className="p-2 bg-slate-700 rounded text-xs space-y-1"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          <p className="text-slate-200">{overlay.text}</p>
                          <p className="text-slate-400">
                            {Math.floor(overlay.startTime)}s - {Math.floor(overlay.endTime)}s
                          </p>
                        </div>
                        <button
                          onClick={() => removeTextOverlay(overlay.id)}
                          className="p-1 hover:bg-slate-600 rounded transition-colors"
                          aria-label={t('common_delete', 'Delete')}
                        >
                          <Trash2 className="w-4 h-4 text-red-400" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Speed Tool */}
          {activeTool === 'speed' && (
            <div className="space-y-4">
              <h3 className="font-semibold text-slate-100">{t('veditor_speed', 'Speed')}</h3>

              <div className="grid grid-cols-3 gap-2">
                {SPEED_PRESETS.map((speed) => (
                  <button
                    key={speed}
                    onClick={() => applySpeedPreset(speed)}
                    className={`px-2 py-2 rounded text-sm font-medium transition-colors ${
                      edit.speed === speed
                        ? 'bg-blue-600 text-white'
                        : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                    }`}
                  >
                    {speed}x
                  </button>
                ))}
              </div>

              <div>
                <label
                  htmlFor="speed-custom"
                  className="block text-sm text-slate-300 mb-2"
                >
                  {t('veditor_custom_speed', 'Custom Speed')}
                </label>
                <div className="flex items-center gap-3">
                  <input
                    id="speed-custom"
                    type="range"
                    min="0.25"
                    max="4"
                    step="0.25"
                    value={edit.speed}
                    onChange={(e) => updateEdit({ speed: parseFloat(e.target.value) })}
                    className="flex-1 h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-blue-500"
                  />
                  <span className="text-sm text-slate-300 w-8 text-right">{edit.speed}x</span>
                </div>
              </div>
            </div>
          )}

          {/* Crop Tool */}
          {activeTool === 'crop' && (
            <div className="space-y-4">
              <h3 className="font-semibold text-slate-100">{t('veditor_crop', 'Crop')}</h3>

              <div>
                <label className="block text-sm text-slate-300 mb-2">
                  {t('veditor_aspect_ratio', 'Aspect Ratio')}
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {getCropPresets(t).map((preset) => (
                    <button
                      key={preset.label}
                      onClick={() => applyCropPreset(preset.ratio)}
                      className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                        edit.cropActive &&
                        edit.cropW > 0 &&
                        Math.abs(edit.cropW / edit.cropH - preset.ratio) < 0.1
                          ? 'bg-blue-600 text-white'
                          : 'bg-slate-700 hover:bg-slate-600'
                      }`}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
              </div>

              {edit.cropActive && (
                <>
                  <div>
                    <label
                      htmlFor="crop-x"
                      className="block text-sm text-slate-300 mb-2"
                    >
                      {t('veditor_x_position', 'X Position')}
                    </label>
                    <input
                      id="crop-x"
                      type="range"
                      min="0"
                      max="100"
                      step="1"
                      value={edit.cropX}
                      onChange={(e) => updateEdit({ cropX: parseInt(e.target.value) })}
                      className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-blue-500"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="crop-y"
                      className="block text-sm text-slate-300 mb-2"
                    >
                      {t('veditor_y_position', 'Y Position')}
                    </label>
                    <input
                      id="crop-y"
                      type="range"
                      min="0"
                      max="100"
                      step="1"
                      value={edit.cropY}
                      onChange={(e) => updateEdit({ cropY: parseInt(e.target.value) })}
                      className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-blue-500"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="crop-w"
                      className="block text-sm text-slate-300 mb-2"
                    >
                      {t('veditor_width', 'Width')}
                    </label>
                    <input
                      id="crop-w"
                      type="range"
                      min="10"
                      max="100"
                      step="1"
                      value={edit.cropW}
                      onChange={(e) => updateEdit({ cropW: parseInt(e.target.value) })}
                      className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-blue-500"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="crop-h"
                      className="block text-sm text-slate-300 mb-2"
                    >
                      {t('veditor_height', 'Height')}
                    </label>
                    <input
                      id="crop-h"
                      type="range"
                      min="10"
                      max="100"
                      step="1"
                      value={edit.cropH}
                      onChange={(e) => updateEdit({ cropH: parseInt(e.target.value) })}
                      className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-blue-500"
                    />
                  </div>
                </>
              )}
            </div>
          )}

          {/* Audio Tool */}
          {activeTool === 'audio' && (
            <div className="space-y-4">
              <h3 className="font-semibold text-slate-100">{t('veditor_audio', 'Audio')}</h3>

              <button
                onClick={() => updateEdit({ muteAudio: !edit.muteAudio })}
                className={`w-full px-3 py-2 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
                  edit.muteAudio
                    ? 'bg-red-600 hover:bg-red-700 text-white'
                    : 'bg-slate-700 hover:bg-slate-600'
                }`}
              >
                {edit.muteAudio ? (
                  <>
                    <VolumeX className="w-4 h-4" />
                    {t('veditor_muted', 'Muted')}
                  </>
                ) : (
                  <>
                    <Volume2 className="w-4 h-4" />
                    {t('veditor_unmuted', 'Unmuted')}
                  </>
                )}
              </button>

              {!edit.muteAudio && (
                <div>
                  <label
                    htmlFor="audio-volume"
                    className="block text-sm text-slate-300 mb-2"
                  >
                    {t('veditor_volume', 'Volume')}
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      id="audio-volume"
                      type="range"
                      min="0"
                      max="200"
                      value={edit.volume}
                      onChange={(e) => updateEdit({ volume: parseInt(e.target.value) })}
                      className="flex-1 h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-blue-500"
                    />
                    <span className="text-sm text-slate-300 w-8 text-right">{edit.volume}%</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Rotate Tool */}
          {activeTool === 'rotate' && (
            <div className="space-y-4">
              <h3 className="font-semibold text-slate-100">{t('veditor_rotate', 'Rotate')}</h3>

              <div className="grid grid-cols-2 gap-2">
                {[0, 90, 180, 270].map((angle) => (
                  <button
                    key={angle}
                    onClick={() => updateEdit({ rotation: angle })}
                    className={`px-2 py-2 rounded text-sm font-medium transition-colors ${
                      edit.rotation === angle
                        ? 'bg-blue-600 text-white'
                        : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                    }`}
                  >
                    {angle}°
                  </button>
                ))}
              </div>

              <button
                onClick={() => updateEdit({ flipH: !edit.flipH })}
                className={`w-full px-3 py-2 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
                  edit.flipH
                    ? 'bg-blue-600 text-white'
                    : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                }`}
              >
                <FlipHorizontal className="w-4 h-4" />
                {t('veditor_flip_horizontal', 'Flip H')}
              </button>

              <button
                onClick={() => updateEdit({ flipV: !edit.flipV })}
                className={`w-full px-3 py-2 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
                  edit.flipV
                    ? 'bg-blue-600 text-white'
                    : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                }`}
              >
                <FlipVertical className="w-4 h-4" />
                {t('veditor_flip_vertical', 'Flip V')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Add missing icon imports
const SkipBack = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    {...props}
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polygon points="19 20 9 12 19 4 19 20"></polygon>
    <line x1="5" y1="4" x2="5" y2="20"></line>
  </svg>
);

const SkipForward = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    {...props}
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polygon points="5 4 15 12 5 20 5 4"></polygon>
    <line x1="19" y1="4" x2="19" y2="20"></line>
  </svg>
);

export default VideoEditorPage;
